
import {defineComponent} from "vue";
import {mapActions as mapActionsP, mapState} from "pinia";
import {CodeDescriptionRestDto} from "@/api/pharma-cms-content-block/models";
import {EnumClassNameCmsEnum} from "@/models/enum/EnumClassNameCmsEnum";
import Subtitle from "@/components/UI/Subtitle.vue";
import BaseOffcanvasButtonClose from "@/components/UI/Bootstrap/Offcanvas/BaseOffcanvasButtonClose.vue";
import InputSelect from "@/components/UI/InputSelect.vue";
import {useToast} from "vue-toastification";
import {useClipboardStore} from "@/stores/ClipboardStore";
import {ContentPageRowRestDtoModel} from "@/models/ContentPageRowRestDtoModel";
import InputNumber from "@/components/UI/InputNumber.vue";
import ColorPicker from "@/components/UI/ColorPicker.vue";
import {BackgroundImageRestDtoModel} from "@/models/api/pharma-cms-content-page/BackgroundImageRestDtoModel";
import InputCheckboxBoolean from "@/components/UI/InputCheckboxBoolean.vue";
import {ColorRestDtoModel} from "@/models/api/ColorRestDtoModel";
import InputImage from "@/components/UI/InputImage.vue";
import {DpModuleEnum} from "@/api/enums/dp-module-enum";
import {EnumRestService} from "@/services/rest/enum/EnumRestService";
import EyebrowDetail from "@/components/layouts/content-pages/wysiwyg/misc/EyebrowDetail.vue";
import BackgroundImageOverlaysDetail from "@/components/layouts/content-pages/wysiwyg/misc/BackgroundImageOverlaysDetail.vue";
import ExtraMetadataSection from "@/components/layouts/content-pages/wysiwyg/misc/ExtraMetadataSection.vue";
import CssDimensionEditor from "@/components/layouts/content-pages/wysiwyg/misc/CssDimensionEditor.vue";
import {FormMixin} from "@/components/base/FormMixin";
import GenericFormWrapper from "@/components/base/GenericFormWrapper.vue";
import {ContentPageRowRestService} from "@/services/rest/cms-content-page/ContentPageRowRestService";
import {normalizeEyebrow} from "@/helpers/functions/content_blocks/normalization";
import {handleSavedSuccessfully} from "@/helpers/toast-helper";

export default defineComponent({
  name: "ContentPageRowDetail",
  emits: ["saved"],
  mixins: [FormMixin],
  components: {
    GenericFormWrapper,
    InputImage,
    InputCheckboxBoolean,
    ColorPicker, InputNumber, InputSelect, BaseOffcanvasButtonClose, Subtitle,
    EyebrowDetail,
    BackgroundImageOverlaysDetail,
    ExtraMetadataSection,
    CssDimensionEditor,
  },
  props: {
    pageId: {
      type: String,
      required: true,
    },
    rowId: {
      type: String,
      required: true,
    },
    /**
     * The identifier as exposed via pharma-content, shown to DP users so they can target the row
     * from custom CSS. It is not part of the row itself, so it is handed down by the page.
     */
    contentId: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      toaster: useToast(),

      row: ContentPageRowRestDtoModel.createWithDefaults(),

      isCustomColorPanelVisible: false,
      isBgImgPanelVisible: false as boolean,
      // Bumped whenever the row is (re)loaded, so the metadata group component
      // remounts and re-captures its reset baseline.
      metadataRevision: 0 as number,
      docType: 'PAGE_IMG' as string,

      enumBackgroundColorCode: [] as CodeDescriptionRestDto[],
      enumContainerTypeCode: [] as CodeDescriptionRestDto[],
      enumHorizontalGutterCode: [] as CodeDescriptionRestDto[],
      enumItemsHorizontalAlignmentCode: [] as CodeDescriptionRestDto[],
      enumItemsVerticalAlignmentCode: [] as CodeDescriptionRestDto[],
      enumMarginCode: [] as CodeDescriptionRestDto[],
      enumPaddingCode: [] as CodeDescriptionRestDto[],
      enumBgImgHorizontalPositionCode: [] as CodeDescriptionRestDto[],
      enumBgImgVerticalPositionCode: [] as CodeDescriptionRestDto[],
      enumBgImgRepeatCode: [] as CodeDescriptionRestDto[],
      enumBgImgSizeCode: [] as CodeDescriptionRestDto[],
      enumBgImgAttachmentCode: [] as CodeDescriptionRestDto[],
    };
  },
  mounted() {
    this.initialize();
  },
  computed: {
    ...mapState(useClipboardStore, ["getClipboardContentPageRow"]),
    DpModuleEnum() {
      return DpModuleEnum;
    },
    activeMetadataLabels(): string[] {
      return this.row.eyebrow ? [this.$t('cms.eyebrow')] : [];
    },
    existingDocumentKey(): string | null {
      return this.row.background_image?.image_key ?? null;
    },
  },
  methods: {
    ...mapActionsP(useClipboardStore, ["copyToClipboardContentPageRow"]),
    async doInitialize(): Promise<void> {
      [
        this.enumBackgroundColorCode,
        this.enumContainerTypeCode,
        this.enumHorizontalGutterCode,
        this.enumItemsHorizontalAlignmentCode,
        this.enumItemsVerticalAlignmentCode,
        this.enumMarginCode,
        this.enumPaddingCode,
        this.enumBgImgHorizontalPositionCode,
        this.enumBgImgVerticalPositionCode,
        this.enumBgImgRepeatCode,
        this.enumBgImgSizeCode,
        this.enumBgImgAttachmentCode,
      ] = await Promise.all([
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.BACKGROUND_COLOR, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.CONTAINER_TYPE, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.GUTTER, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.ITEMS_HORIZONTAL_ALIGNMENT, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.ITEMS_VERTICAL_ALIGNMENT, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.MARGIN, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.PADDING, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.BG_IMG_HOR_POSITION, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.BG_IMG_VER_POSITION, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.BG_IMG_REPEAT, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.BG_IMG_SIZE, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.BG_IMG_ATTACHMENT, DpModuleEnum.CMS),
      ]);
    },
    async doReload(): Promise<void> {
      // cloneFrom() normalizes the response: the row is sent back verbatim on save, so fields the
      // API omits have to get their defaults applied here.
      this.row = ContentPageRowRestDtoModel.cloneFrom(
        await ContentPageRowRestService.getInstance().getContentPageRow(this.pageId, this.rowId)
      );
      this.updatePanelVisibilities();
    },
    async doSubmit(): Promise<void> {
      normalizeEyebrow(this.row.eyebrow);

      if (this.row.background_image && (!this.row.background_image.overlays || this.row.background_image.overlays.length === 0)) {
        this.row.background_image.overlays = null;
      }

      await ContentPageRowRestService.getInstance().updateContentPageRow(this.row, this.pageId, this.rowId);
    },
    async afterSave(): Promise<void> {
      await handleSavedSuccessfully();
      await this.reloadContent();

      // The canvas behind the offcanvas renders this row, so it has to pick up what was actually
      // stored; the page reloads itself on this event.
      this.$emit('saved');
    },
    /**
     * Called by the page every time this offcanvas is opened, so the form always shows what is
     * stored right now.
     *
     * Reopening the same row is how the user cancels: reloadContent() takes the form out of its
     * ready state, which unmounts everything below it, so edits that could not be saved are thrown
     * away instead of being carried into the new session. This replaces a watcher on the row id,
     * which by definition did not fire when the same row was opened again.
     */
    async reload(): Promise<void> {
      await this.reloadContent();
    },
    toggleBackgroundColorType(): void {
      this.isCustomColorPanelVisible = !this.isCustomColorPanelVisible;
      if (this.isCustomColorPanelVisible) {
        this.row.background_color = ColorRestDtoModel.createWithDefaults();
      } else {
        this.row.background_color = undefined;
      }
      this.onFormDataChanged();
    },
    changeBackgroundColorVisible(newValue: boolean) {
      if (newValue) {
        this.row.background_image = BackgroundImageRestDtoModel.createWithDefaults();
      } else {
        this.row.background_image = undefined;
      }
      this.onFormDataChanged();
    },
    async setImageKey(key: string | null): Promise<void> {
      if (this.row.background_image && key) {
        this.row.background_image.image_key = key;
      }
      this.onFormDataChanged();
    },
    onCopyRowMetadata(): void {
      this.copyToClipboardContentPageRow(this.row as ContentPageRowRestDtoModel);
      this.toaster.info(this.$t('cms.successfullyCopiedMetadata'));
    },
    onPasteRowMetadata(): void {
      // cloneFrom() deep copies the clipboard so the row does not alias it on later edits.
      this.row = ContentPageRowRestDtoModel.cloneFrom(this.getClipboardContentPageRow as ContentPageRowRestDtoModel);
      this.updatePanelVisibilities();

      this.toaster.info(this.$t('cms.successfullyPastedMetadata'));
      this.onFormDataChanged();
    },
    updatePanelVisibilities() {
      this.isCustomColorPanelVisible = !!this.row.background_color;
      this.isBgImgPanelVisible = !!this.row.background_image;
      this.metadataRevision++;
    },
  },
});
